import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  definePaymentMethod,
  fetchPaymentMethods,
  fetchPaymentMethodByCode,
  getBanksList,
  verifyBankDetails,
  fetchListCreditDebitCard,
  fetchMidtransToken,
  addNewCreditDebitCard,
  deleteCreditDebitCardSaved,
  fetchPaymentPromotions,
} from '~api/payment';
import { Orders } from '~api/orders';
import { isWeb } from '../../global/screens';
import axios from 'axios';

const initialState = {
  activedPayment: null,
  paymentPromotion: null,
  isDisabledPlaceOrder: false,
  paymentScreen: 'list_payments',
  status: {
    paymentMethods: 'idle',
    paymentMethodByCode: 'idle',
    definePaymentMethod: 'idle',
    listCreditDebitCard: 'idle',
    tokenMidtrans: 'idle',
    deleteDebitCreditCard: 'idle',
    repayCC: 'idle',
    listPaymentPromotions: 'idle',
    verifyBankDetails: 'idle',
    banksList: 'idle',
    midtransToken: 'idle',
  },
  list: [],
  banksList: [],
  informationPaymentMethod: {},
  listBankInstallment: [],
  listCreditDebitCard: [],
  savedtokenFailedCC: null,
  selectedVoucherPayment: null,
  selectedInstallment: {},
  listPaymentPromotions: [],
  listCreditDebitCardTemp: [],
  isStayInCreditCardFailed: false,
  isQrisBNIFailed: false,
};

export const setActivedPaymentRequest = createAsyncThunk(
  'PAYMENT/fetchPaymentMethods',
  async (cartId) => {
    const response = await fetchPaymentMethods(cartId);
    let constructPaymentData = [
      {
        key: 1,
        category: 'last_payment_method',
        list: [],
      },
      {
        key: 2,
        category: 'recommendation_payment_method',
        list: [],
      },
      {
        key: 3,
        category: 'credit_card',
        list: [],
      },
      {
        key: 4,
        category: 'bank_transfer',
        list: [],
      },
      {
        key: 5,
        category: 'other',
        list: [],
      },
    ];

    const responseSavedCard = await fetchListCreditDebitCard();
    const constructResponseSavedCard = responseSavedCard.data.data.map(
      (item) => {
        return {
          ...item,
          code: 'credit_debit_card',
          is_eligible: true,
        };
      },
    );
    const categories = Object.keys(response.data.data);
    const paymentMethods = [];
    constructPaymentData = [
      {
        key: 1,
        category: 'recommendation_payment_method',
        list: [],
        title: 'Recommendation',
      },
      { key: 2, category: 'e_wallet', list: [], title: 'E-Wallet' },
      {
        key: 3,
        category: 'credit_debit_card',
        list: [],
        title: 'Credit/Debit Card',
      },
      {
        key: 4,
        category: 'virtual_account',
        list: [],
        title: 'Virtual Account',
        info: 'Automatic Confirmation',
      },
      { key: 5, category: 'other', list: [], title: 'Others' },
    ];

    categories.forEach((category) => {
      const sortedMethods = response.data.data[category]
        .sort((a, b) => a.position - b.position)
        .map((method) => ({
          ...method,
          category,
        }))
        .filter(
          (method) => method.code !== 'cash_on_delivery' && method.is_active,
        );
      paymentMethods.push(...sortedMethods);

      const categoryData = constructPaymentData.find(
        (item) =>
          item.category === category ||
          (category === 'others' && item.category === 'other'),
      );
      if (categoryData) {
        categoryData.list = sortedMethods;
      }
    });

    if (constructResponseSavedCard.length) {
      constructPaymentData[2].list = [
        ...constructResponseSavedCard,
        ...constructPaymentData[2].list,
      ];

      if (isWeb) {
        const activeSavedCards = constructResponseSavedCard.filter(
          (card) => !card.is_expired,
        );
        const recommendationList = constructPaymentData[0].list;
        const creditCardIndexInRecommendation = recommendationList.findIndex(
          (payment) => payment.code === 'credit_debit_card',
        );

        if (creditCardIndexInRecommendation !== -1 && activeSavedCards.length) {
          const recommendationListWithoutGenericCard = [
            ...recommendationList.slice(0, creditCardIndexInRecommendation),
            ...recommendationList.slice(creditCardIndexInRecommendation + 1),
          ];

          constructPaymentData[0].list = [
            ...activeSavedCards,
            ...recommendationListWithoutGenericCard,
          ];
        }
      }
    }

    return constructPaymentData;
  },
);

export const definePaymentMethodRequest = createAsyncThunk(
  'PAYMENT/definePaymentMethod',
  async ({ paymentCode, payload }) => {
    const response = await definePaymentMethod({ paymentCode, payload });
    if (paymentCode === 'kredivo') {
      response.data.data.map((__, key) => {
        response.data.data[key].isSelected = key === 0 ? true : false;
      });
    }

    if (paymentCode === 'bank_transfer') {
      if (!response.data.data.user_bank_account) {
        response.data.data.hasUserBankAccount = false;
        response.data.data.user_bank_account = {
          account_number: '',
          name: '',
        };
      } else {
        response.data.data.hasUserBankAccount = true;
      }
    }
    return response.data;
  },
);

export const fetchPaymentMethodByCodeRequest = createAsyncThunk(
  'PAYMENT/fetchPaymentMethodByCodeRequest',
  async (payload) => {
    try {
      const response = await fetchPaymentMethodByCode(payload);
      return response.data;
    } catch (err) {
      console.log(err);
      throw new Error('Something went wrong!');
    }
  },
);

export const getBanksListRequest = createAsyncThunk(
  'PAYMENT/getBanksListRequest',
  async (payload) => {
    try {
      const response = await getBanksList(payload);
      return response.data?.data;
    } catch (err) {
      console.log(err);
      throw new Error('Something went wrong!');
    }
  },
);

export const verifyBankDetailsRequest = createAsyncThunk(
  'PAYMENT/verifyBankDetailsRequest',
  async (payload) => {
    const response = await verifyBankDetails(payload);
    return response.data;
  },
);

export const fetchListCreditDebitCardRequest = createAsyncThunk(
  'PAYMENT/fetchListCreditDebitCardRequest',
  async () => {
    const response = await fetchListCreditDebitCard();
    if (Array.isArray(response.data.data) && response.data.data.length) {
      response.data.data.forEach((item, key) => {
        response.data.data[key].indexArray = key;
        response.data.data[key].hasInstallmentPromo = false;
        response.data.data[key].userHasSelectedInstallment = false;
        response.data.data[key].idInstallment = null;
      });
    }
    return response.data;
  },
);

export const fetchMidtransTokenRequest = createAsyncThunk(
  'PAYMENT/fetchMidtransToken',
  async (payload) => {
    const response = await fetchMidtransToken(payload);
    return response.data;
  },
);

export const addNewCreditDebitCardRequest = createAsyncThunk(
  'PAYMENT/addNewCreditDebitCard',
  async (payload) => {
    try {
      const response = await addNewCreditDebitCard(payload);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const serverError = error.response.data;

        throw new Error(serverError.message);
      }

      throw error;
    }
  },
);

export const deleteCreditDebitCardSavedRequest = createAsyncThunk(
  'PAYMENT/deleteDebitCreditCardSaved',
  async (payload) => {
    const response = await deleteCreditDebitCardSaved(payload);
    return response.data;
  },
);

export const repayCC = createAsyncThunk('PAYMENT/repayCC', async (payload) => {
  const response = await Orders.repayCC(payload);
  return response.data;
});

export const getPaymentPromotions = createAsyncThunk(
  'PAYMENT/fetchPaymentPromotions',
  async (payload) => {
    const response = await fetchPaymentPromotions(payload);
    return response.data;
  },
);

const paymentSlice = createSlice({
  name: 'PAYMENT',
  initialState,
  reducers: {
    SET_STATUS_PAYMENT_API(state, { data, name }) {
      state.status[name] = data;
    },
    SET_ACTIVED_PAYMENT(state, action) {
      state.activedPayment = action.data;
    },
    SET_IS_STAY_IN_CREDIT_CARD_FAILED(state, action) {
      state.isStayInCreditCardFailed = action.data;
    },
    SET_QRIS_BNI_FAILED(state, action) {
      state.isQrisBNIFailed = action.data;
    },
    SET_PAYMENT_PROMOTION(state, action) {
      state.paymentPromotion = action.data;
    },
    SET_PAYMENT_SCREEN(state, action) {
      state.paymentScreen = action.data;
    },
    SET_USER_BANK_ACCOUNT(state, action) {
      state.informationPaymentMethod = {
        ...state.informationPaymentMethod,
        user_bank_account: {
          ...state.informationPaymentMethod.user_bank_account,
          ...action.data,
        },
      };
    },
    SET_IS_DISABLED_PLACEORDER(state, action) {
      state.isDisabledPlaceOrder = action.data;
    },
    SET_KREDIVO_INSTALLMENT(state, action) {
      state.informationPaymentMethod.map((info, key) => {
        state.informationPaymentMethod[key].isSelected = false;
      });
      state.informationPaymentMethod[action.key].isSelected = true;
    },
    SET_LIST_PAYMENT_METHOD(state, action) {
      state.list = action.data;
    },
    SET_LIST_DEBIT_CREDIT_CARD(state, action) {
      state.listCreditDebitCard = action.data;
    },
    DELETE_CREDIT_CARD(state, { data }) {
      const copyListCreditDebitCard = [...state.listCreditDebitCard];

      state.listCreditDebitCard.map((card, key) => {
        if (data['_id'] === card['_id']) {
          copyListCreditDebitCard.splice(key, 1);
        }
      });
      state.listCreditDebitCard = [...copyListCreditDebitCard];

      state.listCreditDebitCardTemp = state.listCreditDebitCardTemp.filter(
        (card) => card.masked_card !== data.masked_card,
      );
      state.list = state.list.map((item) => ({
        ...item,
        list: item.list.filter((card) => card.masked_card !== data.masked_card),
      }));
    },
    ADD_CREDIT_CARD(state, { data }) {
      const targetKey = data[0]?.isTemporary
        ? 'listCreditDebitCardTemp'
        : 'listCreditDebitCard';

      const alreadySaved = [
        ...state.listCreditDebitCard,
        ...state.listCreditDebitCardTemp,
      ];

      const seenMaskedCards = new Set();
      const newCards = data.filter((newCard) => {
        if (
          alreadySaved.some(
            (existingCard) => existingCard.masked_card === newCard.masked_card,
          ) ||
          seenMaskedCards.has(newCard.masked_card)
        ) {
          return false;
        }
        seenMaskedCards.add(newCard.masked_card);
        return true;
      });

      if (newCards.length > 0) {
        state[targetKey] = [...state[targetKey], ...newCards];
      }
    },
    SET_TOKEN_FAILED_CC(state, { data }) {
      state.savedtokenFailedCC = data;
    },
    setSelectedPaymentVoucher(state, { data }) {
      state.selectedVoucherPayment = data;
    },
    SET_RESET_PAYMENT(state) {
      state.activedPayment = null;
      state.listCreditDebitCard = [];
      state.list = [];
      state.selectedVoucherPayment = null;
      state.status = {
        paymentMethods: 'idle',
        paymentMethodByCode: 'idle',
        definePaymentMethod: 'idle',
        listCreditDebitCard: 'idle',
        tokenMidtrans: 'idle',
        deleteDebitCreditCard: 'idle',
        repayCC: 'idle',
        listPaymentPromotions: 'idle',
      };
    },

    setSelectedInstallment(state, { data }) {
      state.selectedInstallment = data;
    },
    updateSelectedInstallmentDetail(state, { key }) {
      state.selectedInstallment.installment_term_details.forEach(
        (item, itemKey) => {
          state.selectedInstallment.installment_term_details[
            itemKey
          ].is_selected = false;
        },
      );
      state.selectedInstallment.installment_term_details[
        key
      ].is_selected = true;
    },
    updateUserHasSelectedInstallment(state, { key, value }) {
      state.listCreditDebitCard[key].userHasSelectedInstallment = value;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setActivedPaymentRequest.pending, (state, action) => {
        state.status.paymentMethods = 'loading';
      })
      .addCase(setActivedPaymentRequest.rejected, (state, action) => {
        state.status.paymentMethods = 'error';
      })
      .addCase(setActivedPaymentRequest.fulfilled, (state, action) => {
        state.list = action.payload;

        const creditDebitCard = action.payload.find(
          (item) => item.category === 'credit_debit_card',
        );
        if (creditDebitCard) {
          state.listCreditDebitCard = creditDebitCard.list.filter(
            (card) => !card.is_applicable_for_sociolla_vn,
          );
        }

        state.status.paymentMethods = 'finished';
      })
      .addCase(definePaymentMethodRequest.pending, (state, action) => {
        state.status.definePaymentMethod = 'loading';
      })
      .addCase(definePaymentMethodRequest.rejected, (state, action) => {
        state.status.definePaymentMethod = 'error';
      })
      .addCase(definePaymentMethodRequest.fulfilled, (state, action) => {
        state.informationPaymentMethod = Array.isArray(action.payload.data)
          ? action.payload.data.filter((detail) => !detail.is_installment)
          : action.payload.data;
        if (Array.isArray(action.payload.data) && action.payload.data.length) {
          // check if detail payment method information contains installment promo
          state.listBankInstallment = action.payload.data.filter(
            (detail) => detail.is_installment,
          );
        }

        state.status.definePaymentMethod = 'finished';
      })
      .addCase(fetchListCreditDebitCardRequest.pending, (state, action) => {
        state.status.listCreditDebitCard = 'loading';
      })
      .addCase(fetchListCreditDebitCardRequest.rejected, (state, action) => {
        state.status.listCreditDebitCard = 'error';
      })
      .addCase(fetchListCreditDebitCardRequest.fulfilled, (state, action) => {
        state.listCreditDebitCard = action.payload.data;
        state.status.listCreditDebitCard = 'finished';
      })
      .addCase(fetchMidtransTokenRequest.pending, (state, action) => {
        state.status.midtransToken = 'loading';
      })
      .addCase(fetchMidtransTokenRequest.rejected, (state, action) => {
        state.status.midtransToken = 'error';
      })
      .addCase(fetchMidtransTokenRequest.fulfilled, (state, action) => {
        state.status.midtransToken = 'finished';
      })
      .addCase(addNewCreditDebitCardRequest.pending, (state, action) => {
        state.status.addNewCard = 'loading';
      })
      .addCase(addNewCreditDebitCardRequest.rejected, (state, action) => {
        state.status.addNewCard = 'failed';
      })
      .addCase(addNewCreditDebitCardRequest.fulfilled, (state, action) => {
        state.status.addNewCard = 'succeeded';
      })
      .addCase(deleteCreditDebitCardSavedRequest.pending, (state, action) => {
        state.status.deleteDebitCreditCard = 'loading';
      })
      .addCase(deleteCreditDebitCardSavedRequest.rejected, (state, action) => {
        state.status.deleteDebitCreditCard = 'error';
      })
      .addCase(deleteCreditDebitCardSavedRequest.fulfilled, (state, action) => {
        state.status.deleteDebitCreditCard = 'finished';
      })
      .addCase(fetchPaymentMethodByCodeRequest.pending, (state, action) => {
        state.status.paymentMethodByCode = 'loading';
      })
      .addCase(fetchPaymentMethodByCodeRequest.rejected, (state, action) => {
        state.status.paymentMethodByCode = 'error';
      })
      .addCase(fetchPaymentMethodByCodeRequest.fulfilled, (state, action) => {
        state.activedPayment = action.payload.data[0];
        state.status.paymentMethodByCode = 'finished';
      })
      .addCase(getBanksListRequest.pending, (state, action) => {
        state.status.banksList = 'loading';
      })
      .addCase(getBanksListRequest.rejected, (state, action) => {
        state.status.banksList = 'error';
      })
      .addCase(getBanksListRequest.fulfilled, (state, action) => {
        state.banksList = action.payload;
        state.status.banksList = 'finished';
      })
      .addCase(verifyBankDetailsRequest.pending, (state, action) => {
        state.status.verifyBankDetails = 'loading';
      })
      .addCase(verifyBankDetailsRequest.rejected, (state, action) => {
        state.status.verifyBankDetails = 'error';
      })
      .addCase(verifyBankDetailsRequest.fulfilled, (state, action) => {
        state.status.verifyBankDetails = 'finished';
      })
      .addCase(repayCC.pending, (state, action) => {
        state.status.repayCC = 'loading';
      })
      .addCase(repayCC.rejected, (state, action) => {
        state.status.repayCC = 'error';
      })
      .addCase(repayCC.fulfilled, (state, action) => {
        state.status.repayCC = 'finished';
      })
      .addCase(getPaymentPromotions.pending, (state, action) => {
        state.status.listPaymentPromotions = 'loading';
      })
      .addCase(getPaymentPromotions.rejected, (state, action) => {
        state.status.listPaymentPromotions = 'error';
      })
      .addCase(getPaymentPromotions.fulfilled, (state, action) => {
        state.listPaymentPromotions = action.payload.data;
        state.status.listPaymentPromotions = 'finished';
      });
  },
});

export const {
  SET_STATUS_PAYMENT_API,
  SET_ACTIVED_PAYMENT,
  SET_PAYMENT_PROMOTION,
  SET_PAYMENT_SCREEN,
  SET_INFORMATION_PAYMENT_METHOD,
  SET_LIST_DEBIT_CREDIT_CARD,
  SET_LIST_PAYMENT_METHOD,
  SET_RESET_PAYMENT,
} = paymentSlice.actions;
export default paymentSlice.reducer;
